import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, OnInitEffects, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, filter, map, of, tap } from 'rxjs';
import { LoginResponse } from '../../../core/models/auth.model';
import {
  PRE_REGISTERED_LANDING_ROUTE,
  canAccessStudentPortal,
  isAdmissionsRoute,
  isPreRegisteredStatus,
} from '../../../core/utils/auth-status';
import {
  clearStoredSession,
  readStoredStatus,
  readStoredToken,
  writeStoredSession,
  writeStoredStatus,
} from '../../../core/utils/auth-session-storage';
import { AuthSessionService } from '../../../core/services/auth-session.service';
import { decodeJwtPayload } from '../../../core/utils/jwt';
import { sanitizeReturnUrl } from '../../../core/utils/return-url';
import { AuthService } from '../providers/auth.service';
import { StudentPortalService } from '../../../core/services/student-portal.service';
import { problemDetailMessage } from '../../../core/utils/problem-detail';
import { AuthActions } from './auth.actions';

export class AuthEffects implements OnInitEffects {
  private readonly actions$ = inject(Actions);
  private readonly authService = inject(AuthService);
  private readonly studentPortalService = inject(StudentPortalService);
  private readonly router = inject(Router);
  private readonly authSession = inject(AuthSessionService);

  readonly login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      exhaustMap(({ email, password }) =>
        of(null).pipe(
          tap(() => clearStoredSession()),
          exhaustMap(() =>
            this.authService.login({ email, password }).pipe(
              exhaustMap((response) => {
                return this.resolveStatusFromLoginFallback(response);
              }),
              catchError((error) =>
                of(
                  AuthActions.loginFailure({
                    error: problemDetailMessage(error, 'Error de autenticación'),
                  }),
                ),
              ),
            ),
          ),
        ),
      ),
    ),
  );

  readonly loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        tap(({ token, refreshToken, userName, role, publicId, status }) => {
          writeStoredSession({ token, refreshToken, userName, role, publicId, status });
          const returnUrl = this.returnUrlFromLocation();

          // Pre-registered users only get to return to routes they are allowed to open.
          if (returnUrl && (!isPreRegisteredStatus(status, role) || isAdmissionsRoute(returnUrl))) {
            this.router.navigateByUrl(returnUrl);
            return;
          }

          this.router.navigate([
            isPreRegisteredStatus(status, role) ? PRE_REGISTERED_LANDING_ROUTE : '/dashboard',
          ]);
        }),
      ),
    { dispatch: false },
  );

  readonly refreshStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.init),
      exhaustMap(() => {
        const token = readStoredToken();

        if (!token) {
          return of(AuthActions.statusRefreshed({ status: null }));
        }

        return this.authService.getProfile(token).pipe(
          map((profile) => {
            const status = this.resolveStatus(profile);

            if (status) {
              writeStoredStatus(status);
            }

            return AuthActions.statusRefreshed({
              status: status ?? readStoredStatus(),
            });
          }),
          catchError(() => of(AuthActions.statusRefreshed({ status: readStoredStatus() }))),
        );
      }),
    ),
  );

  // Failure to load permissions must never block or fail login/init; it
  // degrades quietly and the reducer keeps whatever was previously known.
  readonly loadPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadPermissions),
      exhaustMap(() =>
        this.studentPortalService.getMyPermissions().pipe(
          map(({ userType, roles, permissions }) =>
            AuthActions.permissionsLoaded({ userType, roles, permissions }),
          ),
          catchError(() => of(AuthActions.permissionsLoadFailure())),
        ),
      ),
    ),
  );

  readonly loadPermissionsOnLoginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      map(() => AuthActions.loadPermissions()),
    ),
  );

  readonly loadPermissionsOnInit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.init),
      filter(() => Boolean(readStoredToken())),
      map(() => AuthActions.loadPermissions()),
    ),
  );

  readonly logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logout),
        tap(() => this.authSession.logout()),
      ),
    { dispatch: false },
  );

  // Ends the session when a renewal fails for good: same cleanup as a manual logout, but the
  // login page gets the return URL and the reason.
  readonly sessionExpired$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.sessionExpired),
        tap(({ returnUrl }) => this.authSession.expire(returnUrl)),
      ),
    { dispatch: false },
  );

  ngrxOnInitEffects() {
    return AuthActions.init();
  }

  private loginSuccessAction(response: LoginResponse, status: string | null) {
    // Route guards decide access after the authenticated permission request resolves.
    return AuthActions.loginSuccess({
      token: response.token,
      refreshToken: this.refreshTokenFrom(response),
      userName: response.userName,
      role: response.role,
      publicId: response.publicId,
      status,
    });
  }

  private returnUrlFromLocation(): string | null {
    try {
      return sanitizeReturnUrl(this.router.parseUrl(this.router.url).queryParams['returnUrl']);
    } catch {
      return null;
    }
  }

  private refreshTokenFrom(response: LoginResponse): string | null {
    return typeof response.refreshToken === 'string' && response.refreshToken
      ? response.refreshToken
      : null;
  }

  private resolveStatusFromLoginFallback(response: LoginResponse) {
    const status = this.resolveStatus(response);

    if (status) {
      return of(this.loginSuccessAction(response, status));
    }

    return this.authService.getProfile(response.token).pipe(
      map((profile) => this.loginSuccessAction(response, this.resolveStatus(profile))),
      catchError(() => of(this.loginSuccessAction(response, null))),
    );
  }

  private resolveStatus(value: unknown): string | null {
    return (
      this.statusFromRecord(value) ??
      (this.isLoginResponse(value) ? this.statusFromToken(value.token) : null)
    );
  }

  private statusFromRecord(value: unknown): string | null {
    if (!this.isRecord(value)) {
      return null;
    }

    const directStatus = this.stringValue(value, [
      'status',
      'Status',
      'userStatus',
      'UserStatus',
      'user_status',
      'estatus',
      'Estatus',
    ]);

    if (directStatus) {
      return directStatus;
    }

    const nestedStatus = ['user', 'profile', 'data', 'item', 'result']
      .map((key) => this.statusFromRecord(value[key]))
      .find((status): status is string => Boolean(status));

    return nestedStatus ?? null;
  }

  private statusFromToken(token: string): string | null {
    return this.statusFromRecord(decodeJwtPayload(token));
  }

  private stringValue(record: Record<string, unknown>, keys: string[]): string {
    const value = keys
      .map((key) => record[key])
      .find((item) => typeof item === 'string' || typeof item === 'number');

    if (typeof value === 'number') {
      return String(value);
    }

    return typeof value === 'string' ? value.trim() : '';
  }

  private isLoginResponse(value: unknown): value is LoginResponse {
    return this.isRecord(value) && typeof value['token'] === 'string';
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }
}
